import type { ContratCode, DureeCdd, PreferencesOffres, Rythme } from '../types/offres';
import type { SearchTarget } from '../types';
import { Platform } from 'react-native';
import * as Updates from 'expo-updates';
import * as Application from 'expo-application';

/**
 * RÈGLE (03/10/2026) : l'onglet Offres ne s'active en production que par un NOUVEAU BUILD soumis
 * aux stores — jamais par une mise à jour à distance, jamais par un réglage côté serveur. Les mises
 * à jour de production embarquent le code des lots 1 et 2, masqué par cette garde.
 *
 * MOBILE : seul compte le canal GRAVÉ DANS LE BINAIRE (`Updates.channel`, posé au build par
 * `eas.json`). Une mise à jour à distance ne peut pas le changer. La garde précédente lisait
 * d'abord `EXPO_PUBLIC_SENTRY_ENV` : variable de l'environnement EAS, inlinée dans chaque mise à
 * jour — la renommer dans EAS puis publier aurait ouvert l'onglet sans toucher au code. Elle n'est
 * plus lue ici, et un test l'interdit.
 *
 * WEB : il n'y a pas de binaire ni de revue de store. Le site de production a gardé l'écran
 * d'attente jusqu'au 22/10/2026, où Enzo a décidé de l'ouvrir : l'onglet y est actif partout. Le
 * site ne change qu'à un nouvel export suivi d'une mise en ligne manuelle (`scripts/buildWeb.mjs`).
 *
 * ACTIVATION (soumission 1.1.0, décision du 04/10/2026) : sur le canal production, l'onglet s'ouvre
 * à partir du binaire `VERSION_ACTIVATION_OFFRES`. La version NATIVE (`nativeApplicationVersion` :
 * Info.plist, versionName) est gravée dans le binaire ; une mise à jour à distance ne peut pas la
 * changer. Un binaire 1.0.5 qui recevrait plus tard ce code reste donc sur l'écran d'attente.
 * `expo-application` est déjà embarqué dans le binaire 1.0.5 : rien de natif à ajouter.
 *
 * Limite qu'aucune garde en JavaScript ne lève : une mise à jour peut réécrire cette fonction.
 * Seuls la règle et ces tests l'interdisent.
 */
/** Premier binaire de production où l'onglet s'ouvre. */
export const VERSION_ACTIVATION_OFFRES = '1.1.0';

export interface ContexteExecution {
  os: string;
  canalBinaire: string | null;
  hote: string | null;
  /** Version native du binaire ; null si illisible. */
  versionNative: string | null;
}

function contexteCourant(): ContexteExecution {
  return {
    os: Platform.OS,
    canalBinaire: Updates.channel?.trim() || null,
    hote: typeof window !== 'undefined' ? (window.location?.hostname ?? null) : null,
    versionNative: Application.nativeApplicationVersion ?? null,
  };
}

/** « 1.2.3 » ≥ « 1.1.0 », segment par segment. Illisible → faux : dans le doute, l'écran d'attente. */
export function versionAuMoins(version: string | null, minimum: string): boolean {
  const lire = (v: string) => v.split('.').map((x) => Number(x));
  if (!version || !/^\d+(\.\d+)*$/.test(version.trim())) return false;
  const a = lire(version.trim());
  const b = lire(minimum);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const [x, y] = [a[i] ?? 0, b[i] ?? 0];
    if (x !== y) return x > y;
  }
  return true;
}

export function offresActives(contexte: ContexteExecution = contexteCourant()): boolean {
  if (contexte.os === 'web') return true;
  return contexte.canalBinaire !== 'production' || versionAuMoins(contexte.versionNative, VERSION_ACTIVATION_OFFRES);
}

/** Conditions Adzuna : chaque annonce affichée porte la mention « Adzuna », liée à leur site. */
export const URL_ADZUNA = 'https://www.adzuna.fr';

/** Licence des offres France Travail, art. 4 : lien affiché avec chaque offre de cette source. */
export const URL_LICENCE_FRANCE_TRAVAIL =
  'https://francetravail.io/produits-partages/documentation/conditions-dutilisation-api/licence-offres-emploi';

export const CONTRATS: ContratCode[] = ['alternance', 'stage', 'cdi', 'cdd'];
export const RYTHMES: Rythme[] = ['3j2j', '4j1j', '1sem1sem', '2sem2sem', '3sem1sem'];
/** Durées de stage proposées, en mois. « Peu importe » = aucune (null). */
export const DUREES_STAGE_MOIS = [2, 3, 4, 5, 6];
export const DUREES_CDD: DureeCdd[] = ['lt6', '6a12', '12plus'];
export const RAYONS = [10, 20, 30, 50];
export const RAYON_DEFAUT = 20;

// Sous ce score de match, la personne est prévenue AVANT de préparer : « Cette offre est loin de ton
// profil » (décision du 03/10/2026).
export const SEUIL_OFFRE_ELOIGNEE = 50;

/**
 * Disponibilité du profil, en texte libre. Reprise seulement si elle se lit sans ambiguïté comme
 * une date : une date devinée serait une réponse clé inventée, que l'IA reprendrait au lot 2.
 */
export function disponibiliteDepuisTexte(texte: string | null | undefined): string | null {
  const t = (texte ?? '').trim();
  let j: number, m: number, a: number;
  const fr = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t);
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (fr) [j, m, a] = [Number(fr[1]), Number(fr[2]), Number(fr[3])];
  else if (iso) [a, m, j] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  else return null;
  const d = new Date(Date.UTC(a, m - 1, j));
  if (d.getUTCFullYear() !== a || d.getUTCMonth() !== m - 1 || d.getUTCDate() !== j) return null;
  return `${a}-${String(m).padStart(2, '0')}-${String(j).padStart(2, '0')}`;
}

/** Date enregistrée (AAAA-MM-JJ) → texte du champ de saisie (JJ/MM/AAAA). */
export function saisieDepuisIso(iso: string | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

const CONTRATS_PAR_RECHERCHE: Record<SearchTarget, ContratCode[]> = {
  alternance: ['alternance'],
  stage: ['stage'],
  contrat: ['cdi', 'cdd'],
};

/** Préférences de départ, depuis ce que le profil sait déjà. Le permis n'est jamais deviné. */
export function preremplir(
  profil: { city?: string | null; search_target?: SearchTarget | null; availability?: string | null },
  titreCvPrincipal: string | null,
): PreferencesOffres {
  return {
    metier: titreCvPrincipal?.trim() ?? '',
    contrats: profil.search_target ? CONTRATS_PAR_RECHERCHE[profil.search_target] : ['alternance'],
    ville: profil.city?.trim() ?? '',
    rayonKm: RAYON_DEFAUT,
    rythme: null,
    dureeStageMois: null,
    dureeCdd: null,
    permisB: null,
    disponibleLe: disponibiliteDepuisTexte(profil.availability),
  };
}
